import React from "react";
import { styled } from "@mui/material/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { FormLabel } from "@mui/material";
import moment from "moment";
import UPPCalendarIcon from "./UPPCalendarIcon";

moment.updateLocale("en", {
  week: {
    dow: 1,
  },
});

const StyledInputLabel = styled(FormLabel)(() => ({
  display: "block",
  fontFamily: "MyriadPro-Regular, sans-serif",
  fontSize: "16px",
  fontWeight: 700,
  lineHeight: "1.42857143",
  color: "#333",
  marginBottom: "18px",
}));

const StyledDesktopDatePicker = styled(DesktopDatePicker)(() => ({
  width: "100%",
  marginTop: 0,

  "& .MuiPickersInputBase-sectionsContainer": {
    fontSize: "16px",
    textTransform: "lowercase",
    padding: "8px 12px",
  },

  "& .MuiFormControl-root": {
    width: "100%",
  },

  "& .MuiOutlinedInput-root": {
    minHeight: "38px",
    height: "38px",
    borderRadius: "4px",
  },

  "& .MuiInputBase-input": {
    fontFamily: "MyriadPro-Regular, sans-serif",
    fontSize: "16px",
    lineHeight: "1.42857143",
    color: "#333",
    padding: "8px 12px",
  },

  "& .MuiSvgIcon-root": {
    fontSize: "20px",
  },

  "& .MuiInputLabel-root.MuiInputLabel-shrink": {
    fontSize: "14px",
    transform: "translate(14px, -9px) scale(0.9)",
  },
}));

const StyledRangePickersDay = styled(PickersDay, {
  shouldForwardProp: (prop) =>
    prop !== "isInSelectedRange" &&
    prop !== "isInPreviewRange" &&
    prop !== "isExistingRangeWhilePreview",
})(
  ({
    isInSelectedRange,
    isInPreviewRange,
    isExistingRangeWhilePreview,
  }) => ({
    ...(isInSelectedRange && {
      "&&": {
        backgroundColor: "#0072ac !important",
        color: "#fff !important",
        borderRadius: "50%",
      },
    }),

    ...(isInPreviewRange && {
      "&&": {
        backgroundColor: "rgba(33, 107, 165, 0.5) !important",
        color: "#fff !important",
        borderRadius: "50%",
      },
    }),

    ...(isExistingRangeWhilePreview && {
      "&&": {
        backgroundColor: "#f0f0f0 !important",
        color: "#000 !important",
        borderRadius: "50%",
      },
    }),

    "&&.Mui-selected": {
      backgroundColor: "#0072ac !important",
      color: "#fff !important",
    },

    // Only the individual day under the mouse becomes dark blue.
    "&&:hover": {
      backgroundColor: "#004165 !important",
      color: "#fff !important",
    },
  })
);

const getOrderedRange = (start, end) => {
  if (!start || !end) {
    return {
      start: null,
      end: null,
    };
  }

  if (start.isAfter(end, "day")) {
    return {
      start: end,
      end: start,
    };
  }

  return {
    start,
    end,
  };
};

const toMoment = (date) => {
  if (!date) {
    return null;
  }

  if (moment.isMoment(date)) {
    return date.isValid() ? date : null;
  }

  if (date instanceof Date) {
    const momentDate = moment(date);
    return momentDate.isValid() ? momentDate : null;
  }

  const momentDate = moment(date, "DD/MM/YYYY", true);
  return momentDate.isValid() ? momentDate : null;
};

export default function UPPDatePicker({
  onKeyPress = () => {},
  inputLabel,
  value,
  onChange,
  minDate,
  maxDate,
  disableDateSelector,
  disabled,
  name,
  rangeStart,
  rangeEnd,
  selectionMode,
  previewDate,
  onPreviewDateChange,
}) {
  const pickerValue = React.useMemo(() => toMoment(value), [value]);
  const pickerMinDate = React.useMemo(() => toMoment(minDate), [minDate]);
  const pickerMaxDate = React.useMemo(() => toMoment(maxDate), [maxDate]);
  const rangeStartValue = React.useMemo(
    () => toMoment(rangeStart),
    [rangeStart]
  );
  const rangeEndValue = React.useMemo(
    () => toMoment(rangeEnd),
    [rangeEnd]
  );
  const previewValue = React.useMemo(
    () => toMoment(previewDate),
    [previewDate]
  );

  const selectedRange = React.useMemo(
    () => getOrderedRange(rangeStartValue, rangeEndValue),
    [rangeStartValue, rangeEndValue]
  );

  const previewRange = React.useMemo(() => {
    if (!previewValue) {
      return {
        start: null,
        end: null,
      };
    }

    if (selectionMode === "start") {
      return getOrderedRange(previewValue, rangeEndValue);
    }

    if (selectionMode === "end") {
      return getOrderedRange(rangeStartValue, previewValue);
    }

    return {
      start: null,
      end: null,
    };
  }, [previewValue, rangeStartValue, rangeEndValue, selectionMode]);

  const RangeDay = React.useCallback(
    (dayProps) => {
      const {
        day,
        outsideCurrentMonth,
        onMouseEnter,
        onMouseLeave,
        selected,
      } = dayProps;

      const hasSelectedRange =
        !!selectedRange.start && !!selectedRange.end;

      const hasPreviewRange =
        !!previewRange.start && !!previewRange.end;

      const isInSelectedRange =
        hasSelectedRange &&
        !outsideCurrentMonth &&
        day.isSameOrAfter(selectedRange.start, "day") &&
        day.isSameOrBefore(selectedRange.end, "day");

      const isInPreviewRange =
        hasPreviewRange &&
        !isInSelectedRange &&
        !outsideCurrentMonth &&
        day.isSameOrAfter(previewRange.start, "day") &&
        day.isSameOrBefore(previewRange.end, "day");

      const isExistingRangeWhilePreview =
        hasPreviewRange && isInSelectedRange;

      const isCommittedSelectedRange =
        !hasPreviewRange && isInSelectedRange;

      const handleMouseEnter = (event) => {
        if (
          !outsideCurrentMonth &&
          typeof onPreviewDateChange === "function"
        ) {
          onPreviewDateChange(day.format("DD/MM/YYYY"));
        }

        if (typeof onMouseEnter === "function") {
          onMouseEnter(event);
        }
      };

      const handleMouseLeave = (event) => {
        if (typeof onPreviewDateChange === "function") {
          onPreviewDateChange(null);
        }

        if (typeof onMouseLeave === "function") {
          onMouseLeave(event);
        }
      };

      return (
        <StyledRangePickersDay
          {...dayProps}
          selected={!hasPreviewRange && selected}
          isInSelectedRange={isCommittedSelectedRange}
          isInPreviewRange={isInPreviewRange}
          isExistingRangeWhilePreview={isExistingRangeWhilePreview}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        />
      );
    },
    [onPreviewDateChange, previewRange, selectedRange]
  );

  const handleChange = (newValue) => {
    if (typeof onChange !== "function") {
      return;
    }

    if (typeof onPreviewDateChange === "function") {
      onPreviewDateChange(null);
    }

    const formatted =
      newValue &&
      moment.isMoment(newValue) &&
      newValue.isValid()
        ? newValue.format("DD/MM/YYYY")
        : "";

    onChange({
      target: {
        name,
        value: formatted,
      },
    });
  };

  return (
    <>
      {inputLabel && (
        <StyledInputLabel>{inputLabel}</StyledInputLabel>
      )}

      <LocalizationProvider
        dateAdapter={AdapterMoment}
        adapterLocale="en-gb"
      >
        <StyledDesktopDatePicker
          label="dd/mm/yyyy"
          format="DD/MM/YYYY"
          views={["month", "day"]}
          value={pickerValue}
          onChange={handleChange}
          minDate={pickerMinDate}
          maxDate={pickerMaxDate}
          dayOfWeekFormatter={(day) => day.format("dd")}
          disableFuture
          disabled={!!disabled || !!disableDateSelector}
          slots={{
            openPickerIcon: UPPCalendarIcon,
            day: RangeDay,
          }}
          slotProps={{
            textField: {
              onKeyPress,
              name,
              fullWidth: true,
              InputLabelProps: {
                shrink: true,
              },
            },

            popper: {
              sx: {
                zIndex: 9999,

                "& .MuiPaper-root": {
                  width: "330px",
                  minWidth: "330px",
                  fontFamily: "MyriadPro-Regular, sans-serif",
                },

                "& .MuiDateCalendar-root": {
                  width: "330px",
                  maxHeight: "none",
                },

                "& .MuiPickersCalendarHeader-root": {
                  paddingLeft: "16px",
                  paddingRight: "16px",
                  marginTop: "8px",
                },

                "& .MuiPickersCalendarHeader-label": {
                  fontFamily: "MyriadPro-Semibold, sans-serif",
                  fontSize: "16px",
                  color: "#004165",
                },

                "& .MuiDayCalendar-header": {
                  justifyContent: "space-around",
                },

                "& .MuiDayCalendar-weekContainer": {
                  justifyContent: "space-around",
                },

                "& .MuiDayCalendar-weekDayLabel": {
                  width: "40px",
                  height: "46px",
                  fontSize: "13px",
                  fontFamily: "MyriadPro-Semibold, sans-serif",
                  color: "#333",
                },

                "& .MuiPickersDay-root": {
                  width: "40px",
                  height: "37px",
                  fontSize: "13px",
                  fontFamily: "MyriadPro-Regular, sans-serif",
                  color: "#333",
                },

                "& .MuiPickersDay-root:hover": {
                  backgroundColor: "#eef7fb",
                },

                "& .MuiPickersDay-root.Mui-selected": {
                  backgroundColor: "#0072ac",
                  color: "#fff",
                },

                "& .MuiPickersArrowSwitcher-button": {
                  color: "#0072ac",
                },
              },
            },
          }}
        />
      </LocalizationProvider>
    </>
  );
}